import { Box, Typography, Button, Checkbox } from '@mui/material';
import { useState } from 'react';

const mostPopular = [
    {
        id: "mustReads",
        title: "Must Reads",
        subtitle: "Trending News and Analysis for Every Investor",
        description: "FactorAlpha's most popular, market-moving articles on stocks and economic events making headlines around the world.",
        frequency: "Daily",
        enabled: true,
    },
    {
        id: "wsb",
        title: "Wall Street Breakfast",
        subtitle: "Pre-Market News Before Your Day Starts",
        description: "Stay ahead with in-depth insights on the day's top financial and business stories, before they move the market.",
        frequency: "Daily",
        enabled: false,
    },
    {
        id: "stockIdeas",
        title: "Stock Ideas",
        subtitle: "Top Stocks for Active Investors",
        description: "Tap into a steady stream of diverse investing opportunities from across the market.",
        frequency: "Daily",
        enabled: false,
    },
    {
        id: "portfolioDigest",
        title: "Portfolio Digest",
        subtitle: "Essential Updates for Your Watchlist",
        description: "Stay informed about the stocks you follow. Get the latest news and analysis for the stocks in your FactorAlpha portfolio.",
        frequency: "Daily",
        enabled: false,
    },
];

const essentialStrategies = [
    {
        id: "es1",
        title: "Dividend Digest",
        subtitle: "Income Strategies for Long-Term Investors",
        description: "Track high-yield opportunities and dividend announcements from reliable companies building wealth over time.",
        frequency: "Weekly",
        enabled: false,
    },
    {
        id: "es2",
        title: "Tech Trends",
        subtitle: "Innovation Updates for Growth Investors",
        description: "Stay ahead of technology disruptions, emerging trends, and breakthrough innovations in the tech sector.",
        frequency: "Weekly",
        enabled: false,
    },
    {
        id: "es3",
        title: "Value Insights",
        subtitle: "Undervalued Opportunities in Today's Market",
        description: "Discover underpriced stocks with strong fundamentals and long-term growth potential.",
        frequency: "Weekly",
        enabled: false,
    },
];

const marketInsights = [
    {
        id: "mi1",
        title: "Economic Calendar",
        subtitle: "Key Events Moving Markets",
        description: "Get ahead of major economic releases, Fed meetings, and earnings reports that drive market movements.",
        frequency: "Weekly",
        enabled: false,
    },
    {
        id: "mi2",
        title: "Sector Spotlight",
        subtitle: "Industry Analysis and Trends",
        description: "Deep dives into sector performance, emerging trends, and investment opportunities across industries.",
        frequency: "Bi-weekly",
        enabled: false,
    },
    {
        id: "mi3",
        title: "Global Markets",
        subtitle: "International Investment Opportunities",
        description: "Track global market trends, emerging markets, and international investment opportunities.",
        frequency: "Weekly",
        enabled: false,
    },
];

const ToggleSwitch = ({ checked, onChange }) => {
    return (
        <Button
            onClick={onChange}
            sx={{
                position: 'relative',
                display: 'inline-flex',
                height: '24px',
                width: '44px',
                alignItems: 'center',
                borderRadius: '9999px',
                bgcolor: checked ? "var(--color-primary)" : "var(--color-border-secondary)",
                transition: 'all 0.2s',
                minWidth: 'auto',
                p: 0,
                '&:hover': {
                    bgcolor: checked ? "var(--color-primary-dark)" : "var(--color-border-secondary)",
                }
            }}
        >
            <Box
                sx={{
                    display: 'inline-block',
                    height: '16px',
                    width: '16px',
                    transform: checked ? 'translateX(24px)' : 'translateX(4px)',
                    borderRadius: '50%',
                    bgcolor: "#ffffff",
                    transition: 'transform 0.2s'
                }}
            />
        </Button>
    );
};

const NewsletterCard = ({ item, state, onToggle }) => {
    return (
        <Box
            sx={{
                bgcolor: "var(--color-surface)",
                border: "1px solid var(--color-border-primary)",
                borderRadius: 2,
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                transition: 'border-color 0.2s',
                '&:hover': {
                    borderColor: "var(--color-border-secondary)"
                }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                    sx={{
                        width: '6px',
                        height: '36px',
                        bgcolor: "var(--color-primary)",
                        borderRadius: 1,
                        flexShrink: 0,
                        mt: 0.5
                    }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                        <Typography sx={{ fontWeight: 600, color: "var(--color-text-primary)", fontSize: '16px' }}>
                            {item.title}
                        </Typography>
                        <ToggleSwitch
                            checked={state[item.id]}
                            onChange={() => onToggle(item.id)}
                        />
                    </Box>
                    <Typography sx={{ fontSize:"var(--font-size-base)", color: "var(--color-text-muted)" }}>
                        {item.subtitle}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ borderTop: "1px solid var(--color-border-primary)" }} />

            <Typography sx={{ fontSize:"var(--font-size-base)", color: "var(--color-text-muted)", lineHeight: 1.6 }}>
                {item.description}
            </Typography>

            <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 1, fontSize:"var(--font-size-base)" }}>
                <Typography sx={{ color: "var(--color-text-secondary)", fontWeight: 500, fontSize:"var(--font-size-base)" }}>
                    {item.frequency}
                </Typography>
                <Typography sx={{ color: "var(--color-border-secondary)", fontSize:"var(--font-size-base)" }}>|</Typography>
                <Button
                    sx={{
                        color: "var(--color-primary)",
                        fontSize:"var(--font-size-base)",
                        p: 0,
                        minWidth: 'auto',
                        textTransform: 'none',
                        '&:hover': {
                            bgcolor: 'transparent',
                            color: "var(--color-primary)"
                        }
                    }}
                >
                    Preview
                </Button>
            </Box>
        </Box>
    );
};

export default function Newsletter() {

    const [stateMP, setStateMP] = useState(
        mostPopular.reduce((acc, n) => {
            acc[n.id] = n.enabled;
            return acc;
        }, {})
    );

    const [stateES, setStateES] = useState(
        essentialStrategies.reduce((acc, n) => {
            acc[n.id] = n.enabled;
            return acc;
        }, {})
    );

    const [stateMI, setStateMI] = useState(
        marketInsights.reduce((acc, n) => {
            acc[n.id] = n.enabled;
            return acc;
        }, {})
    );

    const handleToggleMP = (id) => {
        setStateMP((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleToggleES = (id) => {
        setStateES((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleToggleMI = (id) => {
        setStateMI((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const allUnsubscribed =
        Object.values(stateMP).every((v) => !v) &&
        Object.values(stateES).every((v) => !v) &&
        Object.values(stateMI).every((v) => !v);

    const handleUnsubscribeAll = (checked) => {
        const updateAll = (state) => {
            const updated = {};
            Object.keys(state).forEach((key) => {
                updated[key] = !checked;
            });
            return updated;
        };
        setStateMP(updateAll(stateMP));
        setStateES(updateAll(stateES));
        setStateMI(updateAll(stateMI));
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: "var(--color-bg-primary)", py: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 2 }}>
                <Typography
                    sx={{
                        fontSize: '18px',
                        fontWeight: 600,
                        color: "var(--color-text-primary)",
                        mb: 0.25,
                        letterSpacing: '-0.01em'
                    }}
                >
                    Free Newsletters
                </Typography>
                <Typography sx={{ fontSize:"var(--font-size-base)", color: "var(--color-text-muted)" }}>
                    Get the latest stock market news and analysis from real investors as it unfolds.
                </Typography>
            </Box>

            <Box sx={{ borderTop: "1px solid var(--color-border-primary)", my: 3 }} />

            <Typography sx={{ fontSize:"var(--font-size-base)", color: "var(--color-text-muted)", mb: 4 }}>
                Click the toggles on/off to subscribe or unsubscribe:
            </Typography>

            {/* Most Popular */}
            <Box sx={{ mb: 5 }}>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: "var(--color-text-primary)", mb: 2 }}>
                    Most Popular
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                    {mostPopular.map((item) => (
                        <NewsletterCard
                            key={item.id}
                            item={item}
                            state={stateMP}
                            onToggle={handleToggleMP}
                        />
                    ))}
                </Box>
            </Box>

            {/* Essential Strategies */}
            <Box sx={{ mb: 5 }}>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: "var(--color-text-primary)", mb: 2 }}>
                    Essential Strategies
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 3 }}>
                    {essentialStrategies.map((item) => (
                        <NewsletterCard
                            key={item.id}
                            item={item}
                            state={stateES}
                            onToggle={handleToggleES}
                        />
                    ))}
                </Box>
            </Box>

            {/* Market Insights */}
            <Box sx={{ mb: 5 }}>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: "var(--color-text-primary)", mb: 2 }}>
                    Market Insights
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 3 }}>
                    {marketInsights.map((item) => (
                        <NewsletterCard
                            key={item.id}
                            item={item}
                            state={stateMI}
                            onToggle={handleToggleMI}
                        />
                    ))}
                </Box>
            </Box>

            {/* Unsubscribe Section */}
            <Box
                sx={{
                    bgcolor: "var(--color-surface)",
                    border: "1px solid var(--color-border-primary)",
                    borderRadius: 2,
                    p: 3,
                    mb: 3
                }}
            >
                <Typography sx={{ fontSize: '18px', fontWeight: 600, color: "var(--color-text-primary)", mb: 2 }}>
                    Unsubscribe From All
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}>
                    <Checkbox
                        checked={allUnsubscribed}
                        onChange={(e) => handleUnsubscribeAll(e.target.checked)}
                        sx={{
                            width: '20px',
                            height: '20px',
                            borderRadius: 0.5,
                            color: "var(--color-border-secondary)",
                            '&.Mui-checked': {
                                color: "var(--color-primary)",
                            }
                        }}
                    />
                    <Typography sx={{ color: "var(--color-text-secondary)", fontSize:"var(--font-size-base)" }}>
                        Stop sending emails from FactorAlpha
                    </Typography>
                </Box>
            </Box>

            {/* Disclaimer */}
            <Box
                sx={{
                    bgcolor: "var(--color-surface)",
                    border: "1px solid var(--color-border-primary)",
                    borderRadius: 2,
                    p: 3
                }}
            >
                <Typography sx={{ fontSize: '18px', fontWeight: 600, color: "var(--color-text-primary)", mb: 1.5 }}>
                    Legal Disclaimer
                </Typography>
                <Typography sx={{ fontSize:"var(--font-size-base)", color: "var(--color-text-muted)", lineHeight: 1.6, textAlign: 'justify' }}>
                    A disclaimer is generally any statement intended to specify or restrict the scope of rights and obligations that may be exercised and enforced by parties in a legally-recognized relationship. By posting your disclaimer prominently and by having the specific legal language you need for your website, the viewer agrees to the terms of the disclaimer. This agreement should include that the visitor assumes any and all risks associated with viewing and/or using any of the information contained on your website.
                </Typography>
                <Typography sx={{ fontSize:"var(--font-size-base)", color: "var(--color-text-muted)", lineHeight: 1.6, textAlign: 'justify', mt: 2 }}>
                    Making a disclaimer sounds easy, right? But honestly it's really complicated. You have to think of every scenario that might happen in the future and take them into consideration while you are making a disclaimer. If you are having problems in making your disclaimer, you can just fill up the fields below, and in an instant, an email will be sent to you with your custom-made disclaimer for your business.
                </Typography>
            </Box>
        </Box>
    );
}